/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import Grid from "@mui/material/Grid";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, TextField, MenuItem } from "@mui/material";

import Panel from "../../../../../components/Panel";
import { tl } from "../../../../../components/translate";
import { Room, RoomFormValues } from "../../../../../interfaces/RoomInterface";
import { usePostRoom, usePatchRoom } from "../../../../../hooks/query/useRooms";
import { ROOM_TYPES } from "../../../../../constants/room";
import { notificationAdd } from "../../../../../actions/notification";
import useCurrentResourceCentre from "../../../../../hooks/useCurrentResourceCentre";
import { useWards } from "../../../../../hooks/query/useWards";

interface RoomCreateEditProps {
  onClose: () => void;
  room?: Partial<Room> | null;
  onSuccess: (data?: Room) => void;
}

// eslint-disable-next-line import/prefer-default-export
export function RoomCreateEdit({ onClose, room, onSuccess }: RoomCreateEditProps): React.ReactNode {
  const isEdit = !!room?.id;
  const dispatch = useDispatch();
  const { data: wards = [] } = useWards();
  const resourceCentre = useCurrentResourceCentre();

  const { mutate: postRoomMutation } = usePostRoom();
  const { mutate: patchRoomMutation } = usePatchRoom();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<RoomFormValues>({
    defaultValues: {
      code: room?.code || "",
      name: room?.name || "",
      type: room?.type || ROOM_TYPES.PRIVATE,
      wardId: room?.wardId || null,
      active: room?.active ?? true,
      resourceCentreId: resourceCentre.id || 0
    }
  });

  const onSubmit = async (data: RoomFormValues) => {
    if (!resourceCentre.id) return;

    if (!data.code || !data.type) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message: "Please fill required fields: Room Code, and Room Type.",
          autoTimeout: true
        })
      );
      return;
    }

    const payload: RoomFormValues = {
      ...data,
      resourceCentreId: resourceCentre.id
    };

    if (isEdit && room?.id) {
      patchRoomMutation(
        { id: room.id, data: payload },
        {
          onSuccess: (response: any) => {
            onSuccess(response?.data);
            onClose();
          }
        }
      );
    } else {
      postRoomMutation(payload, {
        onSuccess: (response: any) => {
          onSuccess(response?.data);
          onClose();
        }
      });
    }
  };

  const footer = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        justifyContent: "flex-end",
        gap: 2
      }}
    >
      <Button onClick={onClose}>{tl("cancel")}</Button>
      <Button type="submit" variant="contained" form="room-form" disabled={isSubmitting}>
        {isEdit ? tl("update") : tl("create")}
      </Button>
    </Box>
  );

  return (
    <Panel title={isEdit ? "Edit Room" : "Create Room"} onClose={onClose} footer={footer}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <form id="room-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    required
                    label="Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                    placeholder="e.g. ICU Room 12"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="code"
                control={control}
                rules={{ required: "Room Code is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    required
                    label="Room Code"
                    error={!!errors.code}
                    helperText={errors.code?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                    placeholder="e.g. ICU-101"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="wardId"
                control={control}
                rules={{ required: "Ward is required" }}
                disabled={isEdit}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                    required
                    label="Ward"
                    error={!!errors.wardId}
                    helperText={errors.wardId?.message}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  >
                    {wards.map((ward) => (
                      <MenuItem key={ward.id} value={ward.id}>
                        {ward.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="type"
                control={control}
                rules={{ required: "Room Type is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                    label="Room Type"
                    required
                    error={!!errors.type}
                    helperText={errors.type?.message}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  >
                    {Object.values(ROOM_TYPES).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </Panel>
  );
}
